import { useEffect, useState } from "react";
import { api, errText } from "../../lib/api";
import { PlacementLink } from "../PlacementLink";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Skeleton } from "../ui/skeleton";
import { toast } from "sonner";
import { Download, Layers, Receipt, Search } from "lucide-react";

export function PaymentLog({ refreshKey }) {
  const [payments, setPayments] = useState(null);
  const [payQ, setPayQ] = useState("");
  const [payFrom, setPayFrom] = useState("");
  const [payTo, setPayTo] = useState("");
  const loadPayments = (f = { q: payQ, from: payFrom, to: payTo }) => {
    const params = new URLSearchParams();
    if (f.q.trim()) params.set("q", f.q.trim());
    if (f.from) params.set("date_from", f.from);
    if (f.to) params.set("date_to", f.to);
    api.get(`/admin/ad-payments?${params.toString()}`).then((r) => setPayments(r.data)).catch((e) => toast.error(errText(e)));
  };
  useEffect(() => {
    loadPayments();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshKey]);
  const exportPayments = async () => {
    try {
      const params = new URLSearchParams();
      if (payQ.trim()) params.set("q", payQ.trim());
      if (payFrom) params.set("date_from", payFrom);
      if (payTo) params.set("date_to", payTo);
      const res = await api.get(`/admin/ad-payments/export.csv?${params.toString()}`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data], { type: "text/csv" }));
      const a = document.createElement("a");
      a.href = url; a.download = "critterposts-ad-payments.csv"; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error(errText(e)); }
  };
  const paymentBundleTotals = {};
  (payments?.payments || []).forEach((p) => {
    if (!p.bundle_id) return;
    const b = paymentBundleTotals[p.bundle_id] || { total: 0, count: 0, first: null };
    b.total += p.amount || 0; b.count += 1; if (!b.first) b.first = p.id;
    paymentBundleTotals[p.bundle_id] = b;
  });
  return (
      <div className="bg-white rounded-2xl border border-slate-200/80 p-5 mt-6" data-testid="payment-log">
        <div className="flex items-center justify-between gap-3 mb-4 flex-wrap">
          <h3 className="font-display font-bold text-[#12234A] flex items-center gap-2"><Receipt size={18} className="text-[#F5551E]" /> Payment log <span className="text-xs font-normal text-slate-400">Interac e-Transfer confirmations</span></h3>
          <div className="flex items-center gap-3">
            {payments && <span className="text-sm font-semibold text-[#12234A]" data-testid="payment-log-total">{payments.count} payment{payments.count === 1 ? "" : "s"} · {payments.currency} ${payments.total_amount.toFixed(2)}</span>}
            <Button variant="outline" size="sm" onClick={exportPayments} disabled={payments && payments.count === 0} data-testid="payment-log-export" className="rounded-xl gap-1.5"><Download size={15} /> Export</Button>
          </div>
        </div>
        <div className="flex items-end gap-2 flex-wrap mb-4">
          <div className="flex-1 min-w-[180px]">
            <Label className="text-[11px] font-semibold text-slate-500">Search</Label>
            <div className="relative mt-1">
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <Input value={payQ} onChange={(e) => setPayQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && loadPayments()} placeholder="Advertiser, email or reference" data-testid="payment-log-search" className="rounded-xl pl-9 h-9" />
            </div>
          </div>
          <div>
            <Label className="text-[11px] font-semibold text-slate-500">From</Label>
            <Input type="date" value={payFrom} onChange={(e) => setPayFrom(e.target.value)} data-testid="payment-log-from" className="rounded-xl h-9 mt-1" />
          </div>
          <div>
            <Label className="text-[11px] font-semibold text-slate-500">To</Label>
            <Input type="date" value={payTo} onChange={(e) => setPayTo(e.target.value)} data-testid="payment-log-to" className="rounded-xl h-9 mt-1" />
          </div>
          <Button onClick={() => loadPayments()} data-testid="payment-log-apply" className="rounded-xl h-9 bg-[#12234A] hover:bg-[#0d1a38] text-white">Filter</Button>
          {(payQ || payFrom || payTo) && <Button variant="ghost" onClick={() => { setPayQ(""); setPayFrom(""); setPayTo(""); loadPayments({ q: "", from: "", to: "" }); }} data-testid="payment-log-clear" className="rounded-xl h-9 text-slate-500">Clear</Button>}
        </div>
        {!payments ? (
          <div className="space-y-2">{[...Array(3)].map((_, i) => <Skeleton key={i} className="h-12 rounded-xl" />)}</div>
        ) : payments.payments.length === 0 ? (
          <p className="text-sm text-slate-400 py-8 text-center">No payments logged yet.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-[11px] uppercase tracking-wide text-slate-400 border-b border-slate-100">
                  <th className="py-2 pr-3 font-semibold">Advertiser</th>
                  <th className="py-2 pr-3 font-semibold">Ad</th>
                  <th className="py-2 pr-3 font-semibold">Reference</th>
                  <th className="py-2 pr-3 font-semibold text-right">Amount</th>
                  <th className="py-2 pr-3 font-semibold">Confirmed by</th>
                  <th className="py-2 font-semibold">When</th>
                </tr>
              </thead>
              <tbody>
                {payments.payments.map((p) => {
                  const b = p.bundle_id ? paymentBundleTotals[p.bundle_id] : null;
                  const isBundle = !!b;
                  const isFirst = isBundle && b.first === p.id;
                  return (
                  <tr key={p.id} className={`border-b border-slate-50 hover:bg-slate-50/60 ${isBundle ? "bg-[#FFF9F6]" : ""}`} data-testid={`payment-row-${p.id}`}>
                    <td className="py-2.5 pr-3"><span className="font-medium text-[#12234A]">{p.advertiser_name}</span><br /><span className="text-[11px] text-slate-400">{p.advertiser_email}</span></td>
                    <td className="py-2.5 pr-3 text-slate-600 max-w-[160px] truncate">{p.ad_title || "—"} <span className="text-[10px] text-slate-400">(<PlacementLink placement={p.placement} className="text-slate-400" newTab />)</span></td>
                    <td className="py-2.5 pr-3 font-mono text-[12px] text-[#12234A] whitespace-nowrap">
                      {p.payment_ref}
                      {isBundle && <span className="ml-1.5 inline-flex items-center gap-1 font-sans text-[10px] font-bold bg-[#F5551E]/10 text-[#F5551E] rounded-full px-1.5 py-0.5" data-testid={`payment-bundle-tag-${p.id}`}><Layers size={10} /> Bundle · {b.count} slots{p.bundle_discount ? ` · ${p.bundle_discount}% off` : ""}</span>}
                    </td>
                    <td className="py-2.5 pr-3 text-right font-semibold text-[#F5551E] whitespace-nowrap">
                      {p.currency} ${(p.amount || 0).toFixed(2)}
                      {isFirst && <span className="block text-[10px] font-normal text-slate-400">{p.currency} ${b.total.toFixed(2)} total</span>}
                    </td>
                    <td className="py-2.5 pr-3 text-slate-600">{p.confirmed_by}</td>
                    <td className="py-2.5 text-slate-500 text-[12px] whitespace-nowrap">{new Date(p.confirmed_at).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" })}</td>
                  </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
  );
}
